import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from 'react';
import { Modal } from './Modal';

/**
 * In-app replacement for window.confirm / window.alert.
 *
 * Native browser prompts on iOS Safari look like phishing dialogs, can't be
 * branded, and are the single fastest "v1 indie SaaS" signal in the product
 * (per audit finding #13). This provider exposes Promise-returning helpers
 * that render the existing <Modal> instead — same shape as the native APIs
 * (boolean for confirm, void for alert) so call-sites swap one-for-one.
 *
 * Usage:
 *   const { confirm, alert } = useConfirm();
 *   if (!(await confirm({ title: 'Delete site?', body: '...', danger: true }))) return;
 *   await alert({ title: 'Nothing to export' });
 */

export interface ConfirmOptions {
  title: string;
  body?: ReactNode;
  confirmLabel?: string;     // default 'Confirm'
  cancelLabel?: string;      // default 'Cancel'
  danger?: boolean;          // red confirm button for destructive actions
}

export interface AlertOptions {
  title: string;
  body?: ReactNode;
  okLabel?: string;          // default 'OK'
}

interface ConfirmContextValue {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  alert: (opts: AlertOptions) => Promise<void>;
}

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

interface QueuedConfirm {
  kind: 'confirm';
  opts: ConfirmOptions;
  resolve: (v: boolean) => void;
}
interface QueuedAlert {
  kind: 'alert';
  opts: AlertOptions;
  resolve: () => void;
}
type Queued = QueuedConfirm | QueuedAlert;

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<Queued | null>(null);

  const confirm = useCallback<ConfirmContextValue['confirm']>((opts) => {
    return new Promise<boolean>((resolve) => {
      setCurrent({ kind: 'confirm', opts, resolve });
    });
  }, []);

  const alertFn = useCallback<ConfirmContextValue['alert']>((opts) => {
    return new Promise<void>((resolve) => {
      setCurrent({ kind: 'alert', opts, resolve });
    });
  }, []);

  function onConfirmClick() {
    if (current?.kind === 'confirm') {
      current.resolve(true);
      setCurrent(null);
    }
  }
  function onCancelClick() {
    if (current?.kind === 'confirm') {
      current.resolve(false);
      setCurrent(null);
    }
  }
  function onAlertClose() {
    if (current?.kind === 'alert') {
      current.resolve();
      setCurrent(null);
    }
  }
  function onBackdropClose() {
    // Backdrop / Esc behave as "cancel" for confirm, "ok" for alert (they
    // already saw the message; closing acknowledges).
    if (current?.kind === 'confirm') {
      current.resolve(false);
    } else if (current?.kind === 'alert') {
      current.resolve();
    }
    setCurrent(null);
  }

  return (
    <ConfirmContext.Provider value={{ confirm, alert: alertFn }}>
      {children}
      {current && (
        <Modal
          open={true}
          title={current.opts.title}
          onClose={onBackdropClose}
        >
          {current.opts.body && (
            <div style={{ marginBottom: 16, color: 'var(--ink-soft)', lineHeight: 1.5 }}>
              {current.opts.body}
            </div>
          )}
          {current.kind === 'confirm' ? (
            <div className="btn-stack">
              <button
                type="button"
                className="btn btn-primary"
                style={current.opts.danger ? { background: 'var(--danger)', borderColor: 'var(--danger)', color: '#fff' } : undefined}
                onClick={onConfirmClick}
                autoFocus
              >
                {current.opts.confirmLabel ?? 'Confirm'}
              </button>
              <button type="button" className="btn btn-ghost" onClick={onCancelClick}>
                {current.opts.cancelLabel ?? 'Cancel'}
              </button>
            </div>
          ) : (
            <div className="btn-stack">
              <button type="button" className="btn btn-primary" onClick={onAlertClose} autoFocus>
                {current.opts.okLabel ?? 'OK'}
              </button>
            </div>
          )}
        </Modal>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmContextValue {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm must be used inside <ConfirmProvider>');
  }
  return ctx;
}
